<template>
  <div class="cert-container">
    <div class="page-header">
      <h2>증명서 신청</h2>
    </div>

    <!-- 신청 폼 -->
    <el-card class="cert-request-card" shadow="never">
      <el-form :model="form" :rules="rules" ref="certForm" @submit.prevent="submitRequest" label-width="110px">
        <el-form-item label="증명서 종류" prop="cert_type">
          <el-radio-group v-model="form.cert_type">
            <el-radio value="EMPLOYMENT">재직증명서</el-radio>
            <el-radio value="CAREER">경력증명서</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="용도" prop="purpose">
          <el-input v-model="form.purpose" placeholder="예: 은행 제출용, 비자 신청용" />
        </el-form-item>

        <el-form-item label="사유">
          <el-input type="textarea" v-model="form.reason" :rows="2" placeholder="결재자 확인용 — 선택 입력" />
        </el-form-item>

        <el-form-item label="개인정보">
          <el-checkbox v-model="form.requires_pii">제출처에서 주민등록번호/주소를 요구합니다</el-checkbox>
          <div v-if="form.requires_pii" class="pii-hint">
            체크하신 경우 결재 승인 완료 후 출력하실 때 직접 입력하시며,
            이 정보는 시스템에 저장되지 않습니다.
          </div>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" native-type="submit" :loading="submitting">신청하기</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 내 신청 목록 -->
    <el-card class="cert-list-card" shadow="never" v-loading="loading">
      <template #header>
        <span>내 신청 내역 <b>{{ requests.length }}건</b></span>
      </template>

      <el-table :data="requests" size="small" style="width: 100%" border empty-text="신청 내역이 없습니다">
        <el-table-column prop="created_at" label="신청일" width="160">
          <template #default="{ row }">{{ formatDate(row.created_at) }}</template>
        </el-table-column>
        <el-table-column prop="cert_type_display" label="종류" width="110" />
        <el-table-column prop="purpose" label="용도" min-width="140">
          <template #default="{ row }">{{ row.purpose || '-' }}</template>
        </el-table-column>
        <el-table-column label="상태" width="100">
          <template #default="{ row }">
            <span class="status-link" @click="showApprovalSteps(row)">{{ mapApprovalStatus(row.approval_status) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="issued_count" label="발급횟수" width="80" align="right" />
        <el-table-column label="작업" width="90" align="center">
          <template #default="{ row }">
            <el-button
              v-if="row.approval_status === 'approved'"
              link type="primary" size="small"
              @click="openIssue(row)"
            >출력</el-button>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 승인 단계별 상태조회 -->
    <el-dialog v-model="approvalDialogVisible" title="승인 단계 목록">
      <el-table :data="approvalSteps" style="width: 100%" border>
        <el-table-column prop="sequence" label="순서" width="80" />
        <el-table-column prop="approver_name" label="승인자" width="200" />
        <el-table-column prop="status" label="상태" width="150">
          <template #default="{ row }">{{ mapApprovalStatus(row.status) }}</template>
        </el-table-column>
        <el-table-column prop="comments" label="코멘트" />
      </el-table>
      <template #footer>
        <el-button @click="approvalDialogVisible = false">닫기</el-button>
      </template>
    </el-dialog>

    <!-- 발급 — 주민번호/주소 입력 (requires_pii인 경우에만) -->
    <el-dialog
      v-model="issueDialogVisible"
      title="증명서 출력"
      width="420px"
      @close="clearPiiForm"
    >
      <template v-if="issueTarget">
        <p v-if="issueTarget.requires_pii" class="pii-notice">
          제출처 요구에 따라 주민등록번호와 주소를 입력해 주세요.
          입력하신 정보는 이 화면을 벗어나면 시스템 어디에도 남지 않습니다.
        </p>
        <el-form v-if="issueTarget.requires_pii" label-width="100px">
          <el-form-item label="주민등록번호">
            <el-input v-model="piiForm.resident_number" placeholder="000000-0000000" />
          </el-form-item>
          <el-form-item label="주소">
            <el-input v-model="piiForm.applicant_address" type="textarea" :rows="2" />
          </el-form-item>
        </el-form>
        <p v-else>바로 출력하시겠습니까?</p>
      </template>
      <template #footer>
        <el-button @click="issueDialogVisible = false">취소</el-button>
        <el-button type="primary" :loading="issuing" @click="confirmIssue">출력</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import axiosInstance from "@/api/axios";
import { ElMessage } from "element-plus";

const form = ref({
  cert_type: "EMPLOYMENT",
  purpose: "",
  reason: "",
  requires_pii: false,
});

const rules = {
  cert_type: [{ required: true, message: "증명서 종류를 선택하세요.", trigger: "change" }],
  purpose: [{ required: true, message: "용도를 입력하세요.", trigger: "blur" }],
};

const certForm = ref(null);
const submitting = ref(false);
const loading = ref(false);
const requests = ref([]);

const approvalDialogVisible = ref(false);
const approvalSteps = ref([]);

// 출력(발급) 다이얼로그 — 주민번호/주소는 이 컴포넌트의 로컬 상태로만 존재하며
// 어디에도 저장하지 않는다. 다이얼로그를 닫으면 즉시 비운다(clearPiiForm).
const issueDialogVisible = ref(false);
const issueTarget = ref(null);
const issuing = ref(false);
const piiForm = ref({ resident_number: "", applicant_address: "" });

const mapApprovalStatus = (status) => {
  const statusMap = {
    pending: "결재중",
    approved: "승인완료",
    rejected: "반려",
    canceled: "취소",
  };
  return statusMap[status] || "알 수 없음";
};

const formatDate = (v) => {
  if (!v) return "-";
  return String(v).slice(0, 16).replace("T", " ");
};

const fetchRequests = async () => {
  loading.value = true;
  try {
    const { data } = await axiosInstance.get("certificate-requests/");
    requests.value = Array.isArray(data) ? data : data?.results || [];
  } catch (e) {
    ElMessage.error("신청 내역을 불러오지 못했습니다.");
  } finally {
    loading.value = false;
  }
};

const submitRequest = async () => {
  certForm.value.validate(async (valid) => {
    if (!valid) return;
    submitting.value = true;
    try {
      await axiosInstance.post("certificate-requests/", form.value);
      ElMessage.success("증명서 신청이 접수되었습니다.");
      form.value = { cert_type: "EMPLOYMENT", purpose: "", reason: "", requires_pii: false };
      await fetchRequests();
    } catch (e) {
      const msg = e?.response?.data?.detail || "신청에 실패했습니다.";
      ElMessage.error(msg);
    } finally {
      submitting.value = false;
    }
  });
};

const showApprovalSteps = (row) => {
  approvalSteps.value = row.approval_steps || [];
  approvalDialogVisible.value = true;
};

const openIssue = (row) => {
  issueTarget.value = row;
  issueDialogVisible.value = true;
};

const clearPiiForm = () => {
  // 다이얼로그가 어떻게 닫히든(취소/성공/바깥클릭) 로컬 상태를 즉시 비운다
  piiForm.value = { resident_number: "", applicant_address: "" };
  issueTarget.value = null;
};

const confirmIssue = async () => {
  if (!issueTarget.value) return;
  if (issueTarget.value.requires_pii &&
      (!piiForm.value.resident_number.trim() || !piiForm.value.applicant_address.trim())) {
    ElMessage.error("주민등록번호와 주소를 모두 입력해 주세요.");
    return;
  }
  issuing.value = true;
  try {
    const payload = issueTarget.value.requires_pii
      ? { resident_number: piiForm.value.resident_number, applicant_address: piiForm.value.applicant_address }
      : {};
    const res = await axiosInstance.post(
      `certificate-requests/${issueTarget.value.id}/issue/`,
      payload,
      { responseType: "blob" }
    );
    window.open(URL.createObjectURL(res.data), "_blank");
    issueDialogVisible.value = false;
    await fetchRequests();
  } catch (e) {
    ElMessage.error("증명서 출력에 실패했습니다.");
  } finally {
    issuing.value = false;
    // 성공/실패 무관하게 입력값은 즉시 비운다 — 저장하지 않는 원칙을 화면단에서도 지킨다
    piiForm.value = { resident_number: "", applicant_address: "" };
  }
};

onMounted(() => {
  fetchRequests();
});
</script>

<style scoped>
.cert-container {
  padding: 12px 20px;
}
.page-header {
  margin-bottom: 10px;
}
.page-header h2 {
  margin: 0;
  font-size: 20px;
}
.cert-request-card,
.cert-list-card {
  margin-bottom: 12px;
}
.pii-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}
.pii-notice {
  margin: 0 0 12px;
  font-size: 13px;
  color: #606266;
}
.status-link {
  cursor: pointer;
  color: #409eff;
}
.muted {
  color: #c0c4cc;
}

@media (max-width: 768px) {
  .cert-container {
    padding: 8px;
  }
}
</style>
